import * as React from 'react';
import classNames from 'classnames';
import useMergedState from 'rc-util/lib/hooks/useMergedState';

import { useToggleStyle } from './style/toggle';

export interface ToggleProps {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  checkedLabel?: React.ReactNode;
  uncheckedLabel?: React.ReactNode;
  onChange?: (checked: boolean) => void;
  className?: string;
  style?: React.CSSProperties;
}

export interface ToggleRef {
  nativeElement: HTMLDivElement;
  focus: VoidFunction;
}

const Toggle = React.forwardRef<ToggleRef, ToggleProps>((props, ref) => {
  const {
    checked,
    defaultChecked = false,
    disabled = false,
    checkedLabel = 'On',
    uncheckedLabel = 'Off',
    onChange,
    className,
    style,
  } = props;

  const [innerChecked, setInnerChecked] = useMergedState(defaultChecked, { value: checked });
  const divRef = React.useRef<HTMLDivElement>(null);

  React.useImperativeHandle(ref, () => ({
    get nativeElement() {
      return divRef.current!;
    },
    focus: () => divRef.current?.focus(),
  }));

  const prefixCls = 'youlify-toggle';
  const [wrapSSR, hashId] = useToggleStyle(prefixCls);

  const handleClick = () => {
    if (disabled) return;
    const next = !innerChecked;
    setInnerChecked(next);
    onChange?.(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleClick();
    }
  };

  const containerCls = classNames(prefixCls, hashId, className, {
    [`${prefixCls}-checked`]: innerChecked,
    [`${prefixCls}-disabled`]: disabled,
  });

  const thumbCls = classNames(`${prefixCls}-thumb`, hashId, {
    [`${prefixCls}-thumb-disabled`]: disabled,
  });

  const labelCls = classNames(`${prefixCls}-label`, hashId, {
    [`${prefixCls}-label-disabled`]: disabled,
  });

  return wrapSSR(
    <div
      ref={divRef}
      className={containerCls}
      style={style}
      role="switch"
      aria-checked={innerChecked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {innerChecked ? (
        <>
          <div className={thumbCls} />
          <div className={labelCls}>{checkedLabel}</div>
        </>
      ) : (
        <>
          <div className={labelCls}>{uncheckedLabel}</div>
          <div className={thumbCls} />
        </>
      )}
    </div>,
  );
});

Toggle.displayName = 'Toggle';

export default Toggle;
